import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Checkbox, Card, message, Typography, Spin } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { hasValidToken } from '../utils/tokenUtils';
import { fetchCurrentUserDetails } from '../services/userService';
import { setUserDetails as setUserDetailsCookie, setAccidentReport as setAccidentReportCookie, clearAccidentReport as clearAccidentReportCookie } from '../utils/cookieUtils';
import TermsAndConditionsModal from '../components/TermsAndConditionsModal';
import '../assets/styles/login.css';
import LoginModal from '../components/LoginModal';
import { createUserNotificationToken } from '../services/firebase-messaging';
import { getAccidentReport } from '../utils/apiService';
import { refreshAccess } from '../utils/accessStore';

import { branding } from '../config/branding';

const { Title, Text } = Typography;

const Login = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [initialChecking, setInitialChecking] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [pendingLoginValues, setPendingLoginValues] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [openLoginModal, setOpenLoginModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const refreshIfNeeded = async () => {
      if (!localStorage.getItem('hardRefreshDone')) {
        await hardRefreshFull();
        localStorage.setItem('hardRefreshDone', 'true');
      }
    };
    refreshIfNeeded();
  }, []);

  // Check for existing valid token and terms acceptance on component mount
  useEffect(() => {
    const checkAuthentication = () => {
      if (hasValidToken()) {
        navigate('/dashboard');
      } else {
        setInitialChecking(false);
      }
    };

    checkAuthentication();
  }, [navigate]);

  // Check if user has accepted terms and conditions
  const hasAcceptedTerms = () => {
    return localStorage.getItem('termsAccepted') === 'true';
  };

  const onFinish = async (values) => {
    // If user hasn't accepted terms, show the modal and store login values for later
    if (!hasAcceptedTerms()) {
      setPendingLoginValues(values);
      setShowTermsModal(true);
      return;
    }

    // Otherwise proceed with login
    await performLogin(values);
  };

  // Handle terms acceptance
  const handleAcceptTerms = () => {
    // Save acceptance to localStorage (most persistent for PWAs)
    localStorage.setItem('termsAccepted', 'true');
    setShowTermsModal(false);

    // Continue with login if we have pending values
    if (pendingLoginValues) {
      performLogin(pendingLoginValues);
      setPendingLoginValues(null);
    }
  };

  // Handle terms decline
  const handleDeclineTerms = () => {
    setShowTermsModal(false);
    setPendingLoginValues(null);
    setLoading(false);
    message.warning('You must accept the Terms and Conditions to use this application.');
  };



  // 🧭 What this does:
  // ✅ Unregisters all service workers (including Firebase messaging worker)
  // ✅ Deletes all caches created via the Cache API (JS, CSS, HTML, offline assets, API responses)
  // ✅ Forces a full reload from the network to fetch the latest app version
  //
  // ❌ What it does NOT clear:
  // - localStorage (tokens, flags, preferences remain)
  // - sessionStorage
  // - IndexedDB (e.g. offline POST queue stays intact)
  // - Cookies
  // - Notification permissions
  // - Installed PWA state (app stays installed)
  //
  // 🔄 After reload:
  // - The PWA service worker auto-installs again from /service-worker.js
  // - Firebase Messaging re-registers automatically
  // - createUserNotificationToken() (called later on login) refreshes push tokens
  //
  async function hardRefreshFull() {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const r of regs) await r.unregister();
      }

      if ('caches' in window) {
        const names = await caches.keys();
        for (const name of names) await caches.delete(name);
      }

      // Force reload from network
      window.location.reload(true);
    } catch (err) {
      console.error('hardRefreshFull failed:', err);
    }
  }


  // Actual login process
  const performLogin = async (values) => {
    setLoading(true);

    try {
      const { username, password } = values;
      const result = await authService.login(username, password, rememberMe);

      if (result.success) {

        const decodedData = result.data;
        const pwaAccess =
          String(decodedData?.PwaAccess ?? 'true').toLowerCase() === 'false'
            ? false
            : true;

        if (!pwaAccess) {
          setOpenLoginModal(true);
          setLoading(false);
          return;
        }

        message.success('Login successful!');

        await refreshAccess();

        try {
          const res = await fetchCurrentUserDetails();
          if (res?.success) {
            setUserDetailsCookie(res.data);
          }
        } catch (e) {
          console.warn('Post-login fetchCurrentUserDetails failed', e);
        }

        try {
          const result = await getAccidentReport();
          const items = Array.isArray(result) ? result : (result?.items || []);
          if (items.length > 0) {
            setAccidentReportCookie({ hasAccess: true });
          } else {
            clearAccidentReportCookie();
          }
        } catch (e) {
          console.warn('Post-login getAccidentReport failed', e);
        }

        try {
          await createUserNotificationToken();
        } catch (e) {
          console.warn('Post-login createUserNotificationToken failed', e);
        }

        navigate('/dashboard');
        localStorage.removeItem('hardRefreshDone');
      } else {
        if (result.status === 401) {
          setLoginError('Invalid username or password');
          message.error('Invalid username or password');
        } else {
          setLoginError(result.message || 'An error occurred during login');
          message.error(result.message || 'An error occurred during login');
        }
      }
    } catch (error) {
      message.error('An unexpected error occurred. Please try again.');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (initialChecking || !localStorage.getItem('hardRefreshDone')) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  return (
    <div className="login-container">
      <Card className="login-card">
        <div className="login-header">
          <div style={{ textAlign: 'center' }}>
            <img src={branding.logoSrc} alt={branding.logoAlt} style={{ height: 100 }} />
          </div>
          <Title level={2}>Welcome Back</Title>
          <Text type="secondary">Sign in to your account</Text>
        </div>

        {/* Terms and Conditions Modal */}
        <TermsAndConditionsModal
          visible={showTermsModal}
          onAccept={handleAcceptTerms}
          onDecline={handleDeclineTerms}
        />

        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          layout="vertical"
          requiredMark={false}

        >
          <Form.Item
            name="username"
            rules={[
              {
                required: true,
                message: 'Please enter your username',
              },
            ]}
          >
            <Input
              prefix={<UserOutlined className="site-form-item-icon" />}
              placeholder="Username"
              size="large"
              onChange={() => setLoginError(null)}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              {
                required: true,
                message: 'Please enter your password',
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="site-form-item-icon" />}
              placeholder="Password"
              size="large"
              onChange={() => setLoginError(null)}
            />
          </Form.Item>

          <Form.Item
            className="login-error"
            name="loginError"
            help={loginError}
            validateStatus="error"
          />

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
            >
              Log in
            </Button>
          </Form.Item>
        </Form>
      </Card>
      <LoginModal open={openLoginModal} onCancel={() => setOpenLoginModal(false)} />
    </div>
  );
};

export default Login;
